<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <b-row>
        <b-col>
          <label class="important-field">{{$t('labels.common.partner')}}</label>
          <multiselect v-model="searchData.partner" :max-height="200"
                       placeholder="Choose partner" label="name" track-by="name"
                       :options="partners" :multiple="false" :close-on-select="true"
                       select-label="" deselect-label="" selected-label=""></multiselect>
        </b-col>
        <b-col>
          <label class="important-field">{{$t('labels.common.company')}}</label>
          <multiselect v-model="searchData.company" :max-height="200"
                       placeholder="Choose company" label="name" track-by="name"
                       :options="offshore_companies" :multiple="false" :close-on-select="true"
                       select-label="" deselect-label="" selected-label=""></multiselect>
        </b-col>
        <b-col>
          <label>{{$t('labels.common.date_range')}}</label>
          <datepicker v-model="searchData.rangeDate" range range-separator="/"
                      lang="en" :first-day-of-week="1"></datepicker>
        </b-col>
        <b-col>
          <label>{{$t('labels.common.currency')}}</label>
          <multiselect v-model="selectedCurrencies" :max-height="200" @input="changeColumnsStatement"
                       placeholder="Choose currencies" label="code" track-by="code"
                       :options="currencies" :multiple="true" :close-on-select="false"
                       select-label="" deselect-label="" selected-label=""></multiselect>
        </b-col>
      </b-row>
    </div>
    <div>
      <div class="d-flex justify-content-end">
        <b-btn variant="outline-secondary" size="sm" @click="excelExport" title="Excel export">
          <icon name="file-excel"></icon>
          {{$t('buttons.common.export')}}
        </b-btn>
      </div>
      <div class="margin-top-10">
        <ag-grid class="ag-theme-balham grid" :gridOptions="gridOptions" :columnDefs="columns"
                 :rowData="rowData" :pinnedTopRowData="pinnedTopRowData" :pinnedBottomRowData="totalData"
                 :postProcessPopup="postProcessPopup"></ag-grid>
      </div>
    </div>
    <div class="balance-info">
      <div>
        <div v-for="item in selectedCurrencies" :key="item.id">
          {{$t('text_content.accountable_currency_balance', {currency: item.code})}} :
          {{item.pre_symbol}}
          {{transformCurrencyValueToLocaleStr(balanceInfo[`balance_acct_${item.code.toLowerCase()}`] || 0)}}
          {{item.post_symbol}}
        </div>
      </div>
      <div v-if="isCurrencySelected">
        <div>
          {{$t('text_content.additional_currency_balance', {currency: `${base_currency.code}`})}}:
          {{base_currency.prefix_symbol}}
          {{transformCurrencyValueToLocaleStr(balanceInfo.balance_addit_base_curr || 0)}}
          {{base_currency.postfix_symbol}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapState, mapGetters } from 'vuex';
  import Datepicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';
  import { gridMixins } from '@/mixins';

  import {
    formatNumber,
    LinkCellRenderer,
    autoResize,
    alignCells
  } from '@/helpers/renderHelpers';
  import {
    cleanFilters,
    handlerResponse,
    transformDateFromDateRangeToStringFormatByIndex,
    addColIdToColumns
  } from '@/helpers/commonHelpers';
  import moment from 'moment';
  import { DATE_FORMAT } from '@/constants';

  import gridProps from '@/gridProps';
  import { PURCHASE, SALE_AGENT } from '@/constants/invoiceTypes';

  const bold = {
    bold: true
  };

  const border = {
    lineStyle: 'Continuous',
    weight: 1
  };

  const emptyExcelCell = {
    type: 'String',
    value: ''
  };

  const excelBorders = {
    borderBottom: border,
    borderLeft: border,
    borderRight: border,
    borderTop: border
  };

  export default {
    mixins: [gridMixins],
    components: {
      Datepicker,
      Multiselect,
      'ag-grid': AgGridVue
    },
    data() {
      return {
        columns: [],
        rowData: [],
        searchData: {
          partner: null,
          company: null,
          rangeDate: []
        },
        selectedCurrencies: [],
        balanceInfo: {},
        totalData: null,
        pinnedTopRowData: null,
        gridOptions: {
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
            cellClassRules: {
              numberCell(params) {
                return typeof params.value === 'number';
              },
              stringCell(params) {
                return typeof params.value === 'string';
              },
              pinnedBottomData(params) {
                return params.node.rowPinned === 'bottom'
              }
            }
          },
          components: {
            'PaymentLink': LinkCellRenderer('id', '/payments/'),
            'InvoiceLink': LinkCellRenderer('id', '/sales-invoice/', true),
            'SalesAgentInvoiceLink': LinkCellRenderer('id', '/sales-agent-invoices/'),
            'PurchaseInvoiceLink': LinkCellRenderer('id', '/purchase-invoice/')
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          excelStyles: [
            {
              id: 'header',
              alignment: {horizontal: 'Center', vertical: 'Center'},
              font: bold,
              borders: excelBorders
            },
            {
              id: 'numberCell',
              alignment: {horizontal: 'Right', vertical: 'Center'},
              numberFormat: {
                format: '#,##0.00'
              },
              borders: excelBorders
            },
            {
              id: 'stringCell',
              alignment: {horizontal: 'Center', vertical: 'Center'},
              borders: excelBorders
            },
            {
              id: 'customHeader',
              font: {...bold, size: 18}
            },
            {
              id: 'customFooter',
              font: {...bold, size: 18}
            },
            {
              id: 'pinnedBottomData',
              font: bold,
              interior: {
                color: '#FCE4D6', pattern: 'Solid'
              }
            }
          ],
          ...gridProps
        },
        requiredFields: ['partner', 'company']
      }
    },
    methods: {
      prepareSearchData() {
        return {
          partner: this.searchData.partner && this.searchData.partner.id,
          company: this.searchData.company && this.searchData.company.id,
          date_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.rangeDate, 0),
          date_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.rangeDate, 1)
        }
      },
      checkSearchFormValidate(searchData) {
        return this.requiredFields.every(item => searchData[item]);
      },
      async search() {
        const params = this.prepareSearchData();
        if (this.checkSearchFormValidate(params)) {
          try {
            const response = await this.$http.get(`accounting/fancy_balance_table/`, {params}).then(handlerResponse);
            const {table, labels, total} = response.data;
            total.date = 'Total';
            if (params.date_after && params.date_before && table.length) {
              this.pinnedTopRowData = [table.shift()];
              this.gridOptions.suppressNoRowsOverlay = true;
            } else {
              this.gridOptions.suppressNoRowsOverlay = false;
              this.pinnedTopRowData = null;
            }
            this.columns = addColIdToColumns(response.used_columns);
            this.changeColumnsStatement();
            this.rowData = table;
            this.generateCellRendererSelector();
            this.totalData = [total];
            this.balanceInfo = labels;
          } catch (e) {
            console.log(e);
          }
        } else {
          const fields = this.requiredFields.map(item => this.$i18n.t(`labels.common.${item}`));
          await this.$swal.fire('', this.$i18n.t('alerts.choose_fields', {fields}), 'warning');
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      changeColumnsStatement() {
        this.columns = this.columns.map(col => {
          if (col.children && col.children.length) {
            col.children.forEach(subCol => {
              subCol.hide = true;
              this.selectedCurrencies.forEach(currency => {
                if (subCol.headerName.includes(currency.code)) {
                  subCol.hide = false;
                }
              });
            });
          }
          return col;
        });
        this.gridOptions.api.setColumnDefs([]);
        this.gridOptions.api.setColumnDefs(this.columns);
        this.$nextTick(() => {
          autoResize(this.gridOptions);
        });
      },
      generateCellRendererSelector() {
        this.columns = this.columns.map(col => {
          if (col.field === 'invoice_number') {
            col.cellRendererSelector = (params) => {
              if (params.data) {
                const invoiceType = params.data.type;
                if (invoiceType === SALE_AGENT) {
                  return {component: 'SalesAgentInvoiceLink'};
                } else if (invoiceType === PURCHASE) {
                  return {component: 'PurchaseInvoiceLink'};
                } else {
                  return {component: 'InvoiceLink'};
                }
              }
            }
          }
          return col;
        });
      },
      transformCurrencyValueToLocaleStr(value) {
        return value.toLocaleString('ru-RU', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).replace(/\s/g, ' ');
      },
      transformRangeDateToString(period) {
        if (period.length) {
          return `${moment(period[0]).format(DATE_FORMAT)} / ${moment(period[1]).format(DATE_FORMAT)}`
        }
        return '';
      },
      renderBalanceRow(label, value, post_symbol) {
        return `${label} : ${value} ${post_symbol}`;

      },
      renderExcelAdditionalInfo() {
        const baseCurrency = this.currencies.find(({code}) => code === `${this.base_currency.code}`);
        const { code, post_symbol } = baseCurrency;
        const additionalLabel = `Additional ${code} balance`;
        const additionalValue = this.transformCurrencyValueToLocaleStr(
          this.balanceInfo.balance_addit_base_curr || 0
        );
        return this.renderBalanceRow(additionalLabel, additionalValue, post_symbol);
      },
      renderExcelAccountableInfo(code, post_symbol) {
        const accountableLabel = `Accountable ${code} balance`;
        const accountableValue = this.transformCurrencyValueToLocaleStr(
          this.balanceInfo[`balance_acct_${code.toLowerCase()}`] || 0
        );
        return this.renderBalanceRow(accountableLabel, accountableValue, post_symbol);
      },
      renderExcelBalanceInfo() {
        const result = this.selectedCurrencies.map((item, index) => {
          const {code, post_symbol} = item;
          const accountInfo = this.renderExcelAccountableInfo(code, post_symbol);
          return [
            {
              data: emptyExcelCell
            },
            {
              styleId: 'customFooter',
              mergeAcross: 3,
              data: {
                type: 'String',
                value: accountInfo
              }
            },
            {
              data: emptyExcelCell
            }
          ]
        });
        if (this.isCurrencySelected) {
          result[0][3] = {
              styleId: 'customFooter',
              data: {
                type: 'String',
                value: this.renderExcelAdditionalInfo()
              }
          }
        }
        return result;
      },
      renderHeaderContent() {
        const date = this.transformRangeDateToString(this.searchData.rangeDate);
        return [
          [
            {
              data: emptyExcelCell,
              mergeAcross: 1,
            },
            {
              mergeAcross: 3,
              styleId: 'customHeader',
              data: {
                type: 'String',
                value: `Partner balance at period ${date}`,
              }
            }
          ],
          []
        ];
      },
      renderFooterContent() {
        return [
          [],
          ...this.renderExcelBalanceInfo()
        ]
      },
      excelExport() {
        const params = {
          columnGroups: true,
          fileName: 'Partners balances',
          // customHeader: this.renderHeaderContent(),
          customFooter: this.renderFooterContent()
        };
        this.gridOptions.api.exportDataAsExcel(params);
      }
    },
    computed: {
      ...mapState('generic', ['currencies', 'currentRoutePermissions']),
      ...mapState('woodware', ['partners', 'base_currency']),
      ...mapGetters('woodware', ['offshore_companies']),
      isCurrencySelected() {
        return this.selectedCurrencies.some(item => item.code === `${this.base_currency.code}`);
      },
      balanceAdditionalBaseCurr() {
        return (this.balanceInfo.balance_addit_base_curr || 0).toLocaleString('ru-RU', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
    },
    async created() {
      this.$store.dispatch('woodware/getPartners');
      await this.$store.cache.dispatch('generic/getCurrencies');
      this.changeColumnsStatement();
    }
  }
</script>

<style scoped>
  .grid {
    height: calc(100vh - 210px);
  }

  .balance-info {
    display: flex;
    justify-content: space-between;
    margin-top: 5px;
    font-size: 14px;
    font-weight: 700;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/accounts/partners-balances/PartnersBalances.vue